import React, { useContext } from "react";
import { Building2, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, NavLink, useNavigate } from "react-router-dom";

import EntityConstants from "../../configs/EntityConstants";
import ApprovalIcon from "../../resources/icons/approvals.svg";
import Template from "../../resources/icons/brush.svg";
import DraftsIcon from "../../resources/icons/folder-favorite.svg";
import Issued from "../../resources/icons/history-timer-icon.svg";
import HomeIcon from "../../resources/icons/presentation-chart-icon.svg";
import Settings from "../../resources/icons/setting-circular-icon.svg";
import TenantIcon from "../../resources/icons/user-round-setting.svg";
import WalletIcon from "../../resources/icons/Wallet.svg";
import WorkspaceIcon from "../../resources/icons/workspace-icon.svg";
import { GET } from "../../service/CRUDService";
import AuthContext from "../../store/auth-context";
import {
  elementTestId,
  automationTestIds,
} from "../../testing/automationTestIds";

const sidebarNavTestId = (nav) =>
  elementTestId("sidebar", "nav", nav.apiName || nav.navKey);

const renderNavIcon = (nav, isActive) => {
  const iconClassName = `transition-all duration-300 ease-in-out ${
    isActive ? "w-7 h-7" : "w-6 h-6"
  }`;

  if (nav.iconComponent) {
    const Icon = nav.iconComponent;
    return <Icon aria-hidden className={iconClassName} strokeWidth={2} />;
  }

  return <img src={nav.icon} alt="" className={iconClassName} />;
};

const Sidebar = () => {
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isOrganisationPage = location.pathname.startsWith("/organisations");
  const isPasswordResetPage = location.pathname.startsWith(
    "/users/update-password",
  );
  if ((isOrganisationPage || isPasswordResetPage) && !authContext.isLoggedIn) {
    return null;
  }

  const handleWorkSpaceClick = (e) => {
    e.preventDefault();
    authContext.ProgrammeOffsetHandler(0);
    authContext.BatchOffsetHandler(0);
    localStorage.setItem("programmeOffset", 0);
    localStorage.setItem("batchOffset", 0);
    navigate("/issuer/workspace");
  };

  const handleIssuedClick = (e) => {
    e.preventDefault();
    authContext.IssuedOffsetHandler(0);
    localStorage.setItem("transactionOffset", 0);
    navigate("/issuer/transaction/all");
  };

  const handleDraftsClick = (e) => {
    e.preventDefault();
    authContext.DraftOffsetHandler(0);
    localStorage.setItem("draftOffset", 0);
    navigate("/issuer/drafts");
  };

  const handleOrganisationClick = (e) => {
    e.preventDefault();
    if (authContext.tenantCode) {
      navigate(`/issuer/tenant/${authContext.tenantCode}/organisations/users`);
    } else {
      const fetchAndNavigate = async () => {
        try {
          const response = await GET(EntityConstants.CURRENT_USER);
          const data = await response.json();
          if (data.tenant_code) {
            navigate(`/issuer/tenant/${data.tenant_code}/organisations/users`);
          } else {
            console.error("Tenant code not found");
            navigate("/issuer/tenant");
          }
        } catch (error) {
          console.error("Error fetching user data for navigation:", error);
          navigate("/issuer/tenant");
        }
      };
      fetchAndNavigate();
    }
  };

  const public_navs = [
    authContext.isLoggedIn
      ? {
          name: t("sidebar:dashboard"),
          navKey: "issuer-home",
          to: "/",
          match: {
            type: "exact",
            path: "/",
          },
          icon: HomeIcon,
        }
      : {
          name: t("sidebar:designs"),
          navKey: "catalog-designs",
          to: "/designs",
          match: {
            type: "exact",
            path: "/designs",
          },
          icon: Template,
        },
  ];

  const all_navs = [
    {
      name: t("sidebar:dashboard"),
      apiName: "Dashboard",
      to: authContext.isLoggedIn ? "/issuer/discover" : "/",
      match: {
        type: "partial",
        path: authContext.isLoggedIn
          ? authContext.userType?.toLowerCase() === "programme_approver" ||
            authContext.userType?.toLowerCase() === "approver" ||
            authContext.userType?.toLowerCase() === "tenant_admin" ||
            authContext.userType?.toLowerCase() === "tenant_user"
            ? "/issuer/discover/approvals"
            : "/issuer/discover"
          : "/",
      },
      icon: HomeIcon,
    },
    {
      name: t("sidebar:designs"),
      apiName: "Designs",
      to: "/designs",
      match: {
        type: "exact",
        path: "/designs",
      },
      icon: Template,
    },
    {
      name: t("sidebar:workspace"),
      apiName: "Workspace",
      to: "/issuer/workspace",
      match: {
        type: "partial",
        path: "/issuer/workspace",
      },
      icon: WorkspaceIcon,
      onClick: handleWorkSpaceClick,
    },
    ...(authContext.sidebarComponents.includes("Holders")
      ? [
          {
            name: t("sidebar:holders"),
            apiName: "Holders",
            to: "/issuer/holders",
            match: {
              type: "partial",
              path: "/issuer/holders",
            },
            iconComponent: Users,
          },
        ]
      : []),
    ...(authContext.sidebarComponents.includes("Draft")
      ? [
          {
            name: t("sidebar:draft"),
            apiName: "Draft",
            to: "/issuer/drafts",
            match: {
              type: "partial",
              path: "/issuer/drafts",
            },
            icon: DraftsIcon,
            onClick: handleDraftsClick,
          },
        ]
      : []),
    {
      name: t("sidebar:history"),
      apiName: "History",
      to: "/issuer/transaction/all",
      match: {
        type: "partial",
        path: "/issuer/transaction/",
      },
      icon: Issued,
      onClick: handleIssuedClick,
    },
    ...(authContext.sidebarComponents.includes("Approvals")
      ? [
          {
            name: t("sidebar:approvals"),
            apiName: "Approvals",
            to: "/issuer/approvals/all",
            match: {
              type: "partial",
              path: "/issuer/approvals",
            },
            icon: ApprovalIcon,
          },
        ]
      : []),

    ...(authContext.sidebarComponents.includes("Organisation")
      ? [
          {
            name: t("sidebar:tenant"),
            apiName: "Organisation",
            to: authContext.tenantCode
              ? `/issuer/tenant/${authContext.tenantCode}/organisations/users`
              : "/issuer/tenant",
            match: {
              type: "partial",
              path: "/issuer/tenant/",
            },
            icon: TenantIcon,
            onClick: handleOrganisationClick,
          },
        ]
      : []),

    ...(authContext.sidebarComponents.includes("Verifier Approvals")
      ? [
          {
            name: t("sidebar:verifier_approvals"),
            apiName: "Verifier Approvals",
            to: "/issuer/verifier-approvals",
            match: {
              type: "partial",
              path: "/issuer/verifier-approvals",
            },
            iconComponent: Building2,
          },
        ]
      : []),
    ...(authContext.sidebarComponents.includes("Tenant")
      ? [
          {
            name: t("sidebar:tenant"),
            apiName: "Tenant",
            to: "/issuer/tenant",
            match: {
              type: "exact",
              path: "/issuer/tenant",
            },
            icon: TenantIcon,
          },
        ]
      : []),
    ...(authContext.sidebarComponents.includes("Finance")
      ? [
          {
            name: t("sidebar:finance"),
            apiName: "Finance",
            to: "/issuer/finance",
            match: {
              type: "partial",
              path: "/issuer/finance",
            },
            icon: WalletIcon,
          },
        ]
      : []),
  ];

  const shouldMatchPath = (nav) => {
    const { match } = nav;

    if (match.type === "exact") {
      return location.pathname === match.path;
    }

    if (match.type === "partial") {
      return location.pathname.startsWith(match.path);
    }

    return false;
  };

  const filteredNavs = authContext.isLoggedIn
    ? all_navs.filter(
        (nav) =>
          nav.name !== "Settings" &&
          (nav.apiName
            ? authContext.sidebarComponents.includes(nav.apiName)
            : authContext.sidebarComponents.includes(nav.name)),
      )
    : public_navs;

  const shouldShowSettings =
    authContext.isLoggedIn &&
    authContext.sidebarComponents.includes("Settings");

  return (
    <div
      id={automationTestIds.sidebar}
      className="flex sm:h-full h-[4rem] text-center bg-cfSecondary items-center sm:items-start 
        py-2 sm:w-[5.688rem] w-full border-t-[1.5px] sm:border-t-0 sm:border-r-[1.5px] fixed sm:relative bottom-0 overflow-x-clip overflow-y-clip sm:flex-col sm:overflow-hidden"
    >
      <div
        className={`flex w-full sm:flex-1 sm:min-h-0 sm:overflow-y-auto py-2 ${
          shouldShowSettings ? "sm:mb-[4.5rem]" : ""
        }`}
      >
        <div className="flex flex-row sm:!flex-col w-full font-CfSecondaryFont gap-y-4 gap-x-0 justify-start text-center">
          <div className="flex w-full">
            <div className="flex flex-row sm:!flex-col w-full font-CfSecondaryFont gap-y-4 gap-x-0 justify-start">
              {(authContext.isLoggedIn ? filteredNavs : public_navs).map(
                (nav) => (
                  <NavLink
                    key={nav.apiName || nav.navKey || nav.name}
                    to={nav.to}
                    title={nav.name}
                    id={sidebarNavTestId(nav)}
                    className="flex flex-grow sm:flex-grow-0 justify-center w-[20%] sm:w-full font-CfPrimaryFont text-sm capitalize py-2 bg-opacity-40 hover:text-cfPrimaryLight hover:no-underline transition-colors duration-200 ease-in-out"
                    onClick={nav.onClick}
                  >
                    <div
                      className={`w-full flex flex-col items-center gap-1 sm:gap-1 justify-center transition-all duration-300 ease-in-out ${
                        shouldMatchPath(nav)
                          ? "font-semibold text-cfPrimaryLight"
                          : "text-cfHeadingText"
                      }`}
                    >
                      {renderNavIcon(nav, shouldMatchPath(nav))}
                      <div
                        className="text-xs sm:text-sm overflow-hidden text-ellipsis transition-colors duration-300 ease-in-out"
                        style={{ maxWidth: "90%" }}
                      >
                        {nav.name}
                      </div>
                    </div>
                  </NavLink>
                ),
              )}
              {shouldShowSettings && (
                <NavLink
                  to="/issuer/settings"
                  title={t("sidebar:settings")}
                  id={elementTestId("sidebar", "nav", "settings-mobile")}
                  className="relative sm:hidden flex flex-grow justify-center w-[20%] font-CfPrimaryFont text-sm capitalize py-2 bg-opacity-40 hover:text-cfPrimaryLight hover:no-underline"
                >
                  <div
                    className={`h-1 w-[20%] absolute bottom-0 transition-all duration-50 ease-in-out ${
                      location.pathname.startsWith("/issuer/settings") &&
                      "bg-cfPrimaryCardHover"
                    }`}
                  />
                  <div
                    className={`w-full flex flex-col items-center gap-1 justify-center ${
                      location.pathname.startsWith("/issuer/settings") &&
                      "font-semibold"
                    }`}
                  >
                    <img src={Settings} alt="" className="w-7 h-8" />
                    <div
                      className="text-xs overflow-hidden text-ellipsis"
                      style={{ maxWidth: "90%" }}
                    >
                      {t("sidebar:settings")}
                    </div>
                  </div>
                </NavLink>
              )}
            </div>
          </div>
        </div>
      </div>

      {shouldShowSettings && (
        <div className="absolute bottom-0 hidden w-full bg-cfSecondary pt-2 sm:!block">
          <NavLink
            to="/issuer/settings"
            title={t("sidebar:settings")}
            id={elementTestId("sidebar", "nav", "settings-desktop")}
            className="flex justify-center w-full font-CfPrimaryFont text-sm capitalize bg-opacity-40 pb-1 hover:text-cfPrimaryLight hover:no-underline transition-colors duration-200 ease-in-out"
          >
            {/* Note:enable this if you want to show the line in sidebar */}
            {/* <div
                className={`h-1 sm:h-14 w-20 sm:w-1 absolute bottom-0 sm:bottom-auto sm:left-0 ${
                  location.pathname.startsWith("/issuer/settings") &&
                  "bg-cfPrimaryCardHover"
                }`}
              /> */}
            <div
              className={`w-full flex flex-col items-center gap-1 justify-center ${
                location.pathname.startsWith("/issuer/settings") &&
                "font-semibold text-cfPrimaryLight"
              }`}
            >
              <img
                src={Settings}
                alt=""
                className={`transition-all duration-300 ease-in-out ${
                  location.pathname.startsWith("/issuer/settings")
                    ? "w-7 h-7"
                    : "w-6 h-6"
                }`}
              />
              <div className="text-xs sm:text-sm">{t("sidebar:settings")}</div>
            </div>
          </NavLink>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
